import { useCallback, useMemo } from 'react';
import { useCopyLink } from '@frameio/shared/routing';
import { getProjectLink, openInNewTab } from '../../utils';
import { STANDARD_RESULT_SOURCE } from '../constants';
import { useQuickSearchModal, useTrackActionedOnResults } from '../contexts';
import type {
  TrackResultSource,
  TrackSearchFilter,
} from '../contexts/SearchAnalyticsContext/types';
import { useProjectResultFieldMatchesV2Fragment } from '../gql/generated/QuickSearchProjectResultV2';
import { getMatchTypeTrackingValues } from '../utils';

type UseProjectActionsParams = {
  appliedFilterTypes: TrackSearchFilter;
  isAdvancedSearch?: boolean;
  projectId: string;
  resultPosition: number;
  resultSource?: TrackResultSource;
  workspaceId?: string;
};

export const useProjectActions = ({
  appliedFilterTypes,
  isAdvancedSearch = false,
  projectId,
  resultPosition,
  resultSource = STANDARD_RESULT_SOURCE,
  workspaceId,
}: UseProjectActionsParams) => {
  const trackActionedOnResults = useTrackActionedOnResults();
  const copyLink = useCopyLink();
  const { setSelectedProject, closeModal } = useQuickSearchModal();

  const {
    data: { matches },
  } = useProjectResultFieldMatchesV2Fragment({
    from: { __typename: 'QuickSearchProjectResult', id: projectId },
  });

  const webSource = isAdvancedSearch ? 'FullScreen' : 'Modal';

  const matchType = useMemo(
    () => getMatchTypeTrackingValues(matches?.nodes ?? []),
    [matches?.nodes]
  );

  const handleCopyLink = useCallback(() => {
    trackActionedOnResults({
      action: 'CopyUrl',
      filterApplied: appliedFilterTypes,
      matchType,
      resultPosition,
      resultSource,
      resultType: 'Project',
      webSource,
    });
    copyLink({
      page: 'folder',
      projectId,
    });
  }, [
    appliedFilterTypes,
    copyLink,
    matchType,
    projectId,
    resultPosition,
    resultSource,
    trackActionedOnResults,
    webSource,
  ]);

  const handleOpenProjectSettings = useCallback(() => {
    trackActionedOnResults({
      action: 'OpenSettings',
      filterApplied: appliedFilterTypes,
      matchType,
      resultPosition,
      resultSource,
      resultType: 'Project',
      webSource,
    });
    setSelectedProject(projectId, workspaceId ?? '');
    closeModal();
  }, [
    appliedFilterTypes,
    closeModal,
    matchType,
    projectId,
    resultPosition,
    resultSource,
    setSelectedProject,
    trackActionedOnResults,
    webSource,
    workspaceId,
  ]);

  const handleOpenInNewTab = useCallback(() => {
    const href = getProjectLink(projectId);
    openInNewTab(href);
  }, [projectId]);

  return {
    handleCopyLink,
    handleOpenProjectSettings,
    handleOpenInNewTab,
    matchType,
  };
};
